import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Printer, Eye, EyeSlash, ArrowRight } from '@phosphor-icons/react';

const DPLUS_LOGO = "/assets/dplus-logo.jpg";

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="login-page">
      {/* Left - Form */}
      <div className="flex items-center justify-center p-8 md:p-16 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <img src={DPLUS_LOGO} alt="Design Plus" className="w-10 h-10 rounded-xl object-cover" />
            <span className="text-xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Design Plus
            </span>
          </div>

          {/* Welcome text */}
          <h1
            className="text-4xl font-bold tracking-tight text-[#0F172A] mb-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Welcome back
          </h1>
          <p className="text-[#475569] mb-10 text-base">
            Sign in to access your pricing dashboard
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-[#0F172A]">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="h-11 rounded-lg border-[#E2E8F0] bg-white focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-1 transition-all duration-200"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-[#0F172A]">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="h-11 rounded-lg border-[#E2E8F0] bg-white pr-10 focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-1 transition-all duration-200"
                />
                <button
                  type="button"
                  data-testid="toggle-password-btn"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569] transition-colors"
                >
                  {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm animate-scale-in">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-11 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold transition-all duration-200 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight size={18} weight="bold" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-[#94A3B8]">
            Pricing Dashboard by Design Plus
          </p>
        </div>
      </div>

      {/* Right - Hero Image */}
      <div
        className="hidden md:block login-hero"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1610187390406-9d24b55ea697?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxwcmludGluZyUyMHByb2R1Y3RzJTIwYnVzaW5lc3MlMjBjYXJkcyUyMGZseWVyfGVufDB8fHx8MTc3NjM2MzQ4NXww&ixlib=rb-4.1.0&q=85)',
        }}
      >
        <div className="relative z-10 flex flex-col justify-end h-full p-12 text-white">
          <h2 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Print Products Pricing
          </h2>
          <p className="text-white/80 text-lg max-w-md">
            Configure product options, view pricing tiers, and manage your print catalog.
          </p>
        </div>
      </div>
    </div>
  );
}
